import type {
  DictationMode,
  AppointmentDto,
  AuditLogDto,
  AvailabilityDto,
  ChamberActiveDoctor,
  ChamberDto,
  ClinicBrief,
  ClinicDto,
  ConsultationDto,
  DailyReportRow,
  DashboardMetrics,
  DictationCapabilityDto,
  DictationResultDto,
  DisplayConfigDto,
  DisplayLoginResponse,
  DisplaySessionCreatedDto,
  DisplaySessionDto,
  DoctorBrief,
  DoctorDto,
  DoctorQueueSummary,
  DoctorReportRow,
  DoctorScheduleDto,
  LoginResponse,
  Paginated,
  PatientBrief,
  PatientDto,
  PeakHourRow,
  PublicChamberDto,
  PublicDoctorDto,
  QueuePublicState,
  QueueStaffState,
  QueueStatisticsDto,
  QuickWalkInResult,
  SessionUser,
  TimeMetricReport,
  TokenAction,
  TokenDto,
  UpdateRxRequest,
  UserDto,
} from '@docsystem/shared';
import { api, buildQuery } from '@/lib/api-client';

// ── Auth ──────────────────────────────────────────────────────────────────

export interface MeResponse {
  user: SessionUser;
  clinic: ClinicBrief;
}

export const authService = {
  /** `username` also accepts an email address — the API resolves either. */
  login: (username: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { username, password }),
  /** Room-kiosk sign-in: the doctor taps their name, then types their PIN. */
  chamberLogin: (doctorId: string, pin: string) =>
    api.post<LoginResponse>('/auth/chamber-login', { doctorId, pin }),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
  me: () => api.get<MeResponse>('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>('/auth/change-password', { currentPassword, newPassword }),
};

// ── Clinic ────────────────────────────────────────────────────────────────

export const clinicService = {
  current: () => api.get<ClinicDto>('/clinics/current'),
  update: (body: Record<string, unknown>) => api.patch<ClinicDto>('/clinics/current', body),
};

// ── Doctors ───────────────────────────────────────────────────────────────

export const doctorService = {
  list: (includeInactive = false) =>
    api.get<DoctorDto[]>(`/doctors${buildQuery({ includeInactive })}`),
  briefs: () => api.get<DoctorBrief[]>('/doctors/briefs'),
  listPublic: (clinicCode: string) =>
    api.get<PublicDoctorDto[]>(`/doctors/public/${encodeURIComponent(clinicCode)}`),
  get: (id: string) => api.get<DoctorDto>(`/doctors/${id}`),
  create: (body: Record<string, unknown>) => api.post<DoctorDto>('/doctors', body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<DoctorDto>(`/doctors/${id}`, body),
  remove: (id: string) => api.delete<void>(`/doctors/${id}`),
};

// ── Chambers ──────────────────────────────────────────────────────────────

export const chamberService = {
  list: (includeInactive = false) =>
    api.get<ChamberDto[]>(`/chambers${buildQuery({ includeInactive })}`),
  listPublic: (clinicCode: string) =>
    api.get<PublicChamberDto[]>(`/chambers/public/${encodeURIComponent(clinicCode)}`),
  getActiveDoctor: (chamberId: string, clinicId?: string) =>
    api.get<ChamberActiveDoctor>(`/chambers/${chamberId}/active-doctor${buildQuery({ clinicId })}`),
  occupancy: () => api.get<ChamberActiveDoctor[]>('/chambers/occupancy'),
  myCheckIn: () => api.get<ChamberActiveDoctor | null>('/chambers/my-check-in'),
  checkIn: (chamberId: string) =>
    api.post<ChamberActiveDoctor>(`/chambers/${chamberId}/check-in`),
  checkOut: () =>
    api.post<{ ok: boolean }>('/chambers/check-out'),
  create: (body: Record<string, unknown>) => api.post<ChamberDto>('/chambers', body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<ChamberDto>(`/chambers/${id}`, body),
  remove: (id: string) => api.delete<void>(`/chambers/${id}`),
};

// ── Patients ──────────────────────────────────────────────────────────────

export const patientService = {
  search: (params: Record<string, unknown>) =>
    api.get<Paginated<PatientDto>>(`/patients${buildQuery(params)}`),
  lookupByMobile: (mobile: string) =>
    api.get<PatientBrief[]>(`/patients/lookup${buildQuery({ mobile })}`),
  get: (id: string) => api.get<PatientDto>(`/patients/${id}`),
  tokens: (id: string, params: Record<string, unknown> = {}) =>
    api.get<Paginated<TokenDto>>(`/patients/${id}/tokens${buildQuery(params)}`),
  create: (body: Record<string, unknown>) => api.post<PatientDto>('/patients', body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<PatientDto>(`/patients/${id}`, body),
  remove: (id: string) => api.delete<void>(`/patients/${id}`),
};

// ── Appointments ──────────────────────────────────────────────────────────

export const appointmentService = {
  search: (params: Record<string, unknown>) =>
    api.get<Paginated<AppointmentDto>>(`/appointments${buildQuery(params)}`),
  get: (id: string) => api.get<AppointmentDto>(`/appointments/${id}`),
  create: (body: Record<string, unknown>) => api.post<AppointmentDto>('/appointments', body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<AppointmentDto>(`/appointments/${id}`, body),
  checkIn: (id: string) => api.post<AppointmentDto>(`/appointments/${id}/check-in`),
  cancel: (id: string, reason: string) =>
    api.post<AppointmentDto>(`/appointments/${id}/cancel`, { reason }),
  reschedule: (id: string, body: Record<string, unknown>) =>
    api.post<AppointmentDto>(`/appointments/${id}/reschedule`, body),
  quickWalkIn: (body: Record<string, unknown>) =>
    api.post<QuickWalkInResult>('/appointments/quick-walk-in', body),
};

// ── Schedules ─────────────────────────────────────────────────────────────

export const scheduleService = {
  listAll: () => api.get<DoctorScheduleDto[]>('/schedules'),
  byDoctor: (doctorId: string) => api.get<DoctorScheduleDto[]>(`/schedules/${doctorId}`),
  availability: (doctorId: string, date: string) =>
    api.get<AvailabilityDto>(`/schedules/${doctorId}/availability${buildQuery({ date })}`),
  create: (body: Record<string, unknown>) => api.post<DoctorScheduleDto>('/schedules', body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<DoctorScheduleDto>(`/schedules/id/${id}`, body),
  remove: (id: string) => api.delete<void>(`/schedules/id/${id}`),
};

// ── Queues & tokens ───────────────────────────────────────────────────────

export const queueService = {
  board: (date?: string) => api.get<DoctorQueueSummary[]>(`/queues${buildQuery({ date })}`),
  get: (doctorId: string, date?: string) =>
    api.get<QueueStaffState>(`/queues/${doctorId}${buildQuery({ date })}`),
  callNext: (doctorId: string) => api.post<QueueStaffState>(`/queues/${doctorId}/call-next`),
  callSpecific: (doctorId: string, tokenId: string) =>
    api.post<QueueStaffState>(`/queues/${doctorId}/call/${tokenId}`),
  pause: (doctorId: string) => api.post<QueueStaffState>(`/queues/${doctorId}/pause`),
  resume: (doctorId: string) => api.post<QueueStaffState>(`/queues/${doctorId}/resume`),
  open: (doctorId: string) => api.post<QueueStaffState>(`/queues/${doctorId}/open`),
  close: (doctorId: string) => api.post<QueueStaffState>(`/queues/${doctorId}/close`),
  reset: (queueId: string, reason: string) =>
    api.post<QueueStaffState>(`/queues/id/${queueId}/reset`, { reason }),
  reconcile: (queueId: string) => api.post<QueueStaffState>(`/queues/id/${queueId}/reconcile`),
};

const TOKEN_ACTION_PATH: Record<Exclude<TokenAction, 'call'>, string> = {
  recall: 'recall',
  start: 'start',
  complete: 'complete',
  skip: 'skip',
  noShow: 'no-show',
  cancel: 'cancel',
  requeue: 'requeue',
};

export const tokenService = {
  today: (doctorId?: string) => api.get<TokenDto[]>(`/tokens/today${buildQuery({ doctorId })}`),
  current: (doctorId: string) =>
    api.get<TokenDto | null>(`/tokens/current${buildQuery({ doctorId })}`),
  next: (doctorId: string) => api.get<TokenDto | null>(`/tokens/next${buildQuery({ doctorId })}`),
  get: (id: string) => api.get<TokenDto & { availableActions: TokenAction[] }>(`/tokens/${id}`),
  act: (id: string, action: Exclude<TokenAction, 'call'>, reason?: string) =>
    api.post<QueueStaffState>(`/tokens/${id}/${TOKEN_ACTION_PATH[action]}`, reason ? { reason } : undefined),
};

// ── Consultations / Rx pad ────────────────────────────────────────────────

export const consultationService = {
  /** Is dictation configured at all? Decides whether the mic button renders. */
  capability: () => api.get<DictationCapabilityDto>('/consultations/dictation/capability'),

  /** Idempotent — safe to call every time the consultation screen mounts. */
  open: (tokenId: string) => api.post<ConsultationDto>('/consultations/open', { tokenId }),

  get: (id: string) => api.get<ConsultationDto>(`/consultations/${id}`),
  byToken: (tokenId: string) =>
    api.get<ConsultationDto | null>(`/consultations/by-token/${tokenId}`),

  history: (patientId: string, limit?: number) =>
    api.get<ConsultationDto[]>(
      `/consultations/patient/${patientId}/history${buildQuery({ limit })}`,
    ),

  /**
   * One dictation pass. The field name `audio` must match the FileInterceptor on
   * the server; the filename carries the extension Whisper uses to detect the codec.
   */
  dictate: (id: string, audio: Blob, filename: string, mode: DictationMode) => {
    const form = new FormData();
    form.append('audio', audio, filename);
    form.append('mode', mode);
    return api.postForm<DictationResultDto>(`/consultations/${id}/dictate`, form);
  },

  update: (id: string, patch: UpdateRxRequest) =>
    api.patch<ConsultationDto>(`/consultations/${id}`, patch),

  sign: (id: string) => api.post<ConsultationDto>(`/consultations/${id}/sign`),
};

// ── Display ───────────────────────────────────────────────────────────────

export const displayService = {
  // These use the display credential, not the staff access token.
  queue: (doctorId: string) =>
    api.get<QueuePublicState>(`/display/queue/${doctorId}`, { asDisplay: true }),
  chamberQueue: (chamberId: string) =>
    api.get<{ activeDoctor: ChamberActiveDoctor; queue: QueuePublicState | null }>(
      `/display/chamber/${chamberId}`,
      { asDisplay: true },
    ),
  config: () => api.get<DisplayConfigDto>('/display/config', { asDisplay: true }),

  /** A screen signs in as the room it hangs outside — replaces carrying a ?dt= URL. */
  screenLogin: (username: string, password: string, deviceName?: string) =>
    api.post<DisplayLoginResponse>('/display/login', { username, password, deviceName }),
  setScreenCredentials: (
    chamberId: string,
    body: { username: string; password: string; signOutExistingScreens?: boolean },
  ) => api.post<{ revokedScreens: number }>(`/display/chambers/${chamberId}/credentials`, body),
  clearScreenCredentials: (chamberId: string) =>
    api.delete<{ revokedScreens: number }>(`/display/chambers/${chamberId}/credentials`),
  listSessions: () => api.get<DisplaySessionDto[]>('/display/sessions'),
  createSession: (body: Record<string, unknown>) =>
    api.post<DisplaySessionCreatedDto>('/display/sessions', body),
  revokeSession: (id: string) => api.delete<void>(`/display/sessions/${id}`),
};

// ── Users ─────────────────────────────────────────────────────────────────

export const userService = {
  search: (params: Record<string, unknown>) =>
    api.get<Paginated<UserDto>>(`/users${buildQuery(params)}`),
  get: (id: string) => api.get<UserDto>(`/users/${id}`),
  create: (body: Record<string, unknown>) => api.post<UserDto>('/users', body),
  update: (id: string, body: Record<string, unknown>) => api.patch<UserDto>(`/users/${id}`, body),
  resetPassword: (id: string, newPassword: string) =>
    api.post<{ ok: true }>(`/users/${id}/reset-password`, { newPassword }),
  /** `null` removes the PIN, taking that doctor off the kiosk keypad flow. */
  setPin: (id: string, pin: string | null) => api.post<{ ok: true }>(`/users/${id}/pin`, { pin }),
  remove: (id: string) => api.delete<void>(`/users/${id}`),
};

// ── Reports ───────────────────────────────────────────────────────────────

export const reportService = {
  dashboard: (date?: string) =>
    api.get<DashboardMetrics>(`/reports/dashboard${buildQuery({ date })}`),
  daily: (params: Record<string, unknown>) =>
    api.get<DailyReportRow[]>(`/reports/daily${buildQuery(params)}`),
  byDoctor: (params: Record<string, unknown>) =>
    api.get<DoctorReportRow[]>(`/reports/doctor${buildQuery(params)}`),
  waitingTime: (params: Record<string, unknown>) =>
    api.get<TimeMetricReport>(`/reports/waiting-time${buildQuery(params)}`),
  consultationTime: (params: Record<string, unknown>) =>
    api.get<TimeMetricReport>(`/reports/consultation-time${buildQuery(params)}`),
  peakHours: (params: Record<string, unknown>) =>
    api.get<PeakHourRow[]>(`/reports/peak-hours${buildQuery(params)}`),
  queueStatistics: (params: Record<string, unknown>) =>
    api.get<QueueStatisticsDto>(`/reports/queue-statistics${buildQuery(params)}`),
};

// ── Audit ─────────────────────────────────────────────────────────────────

export const auditService = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<AuditLogDto>>(`/audit-logs${buildQuery(params)}`),
};
